import { createRef, PureComponent, ReactNode, RefObject } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { withRouter } from 'next/router';
import { WithRouterProps } from 'next/dist/client/with-router';
import Router from 'next/router';
import NProgress from 'nprogress';

import cx from 'classnames';
import get from 'lodash/get';
import throttle from 'lodash/throttle';
import { differenceInDays } from 'date-fns';
import localForage from 'localforage';
import { ThemeProvider } from 'styled-components';
import {
  Theme,
  GlobalStyle,
  NotificationBar,
  Footer,
  SideNav,
  BusinessOnboardingPopUp,
} from 'styled';

import sideNavigationByOrder from 'state/selectors/sideNavigationByOrder';
import organizedTopic from 'state/selectors/organizedTopic';

import {
  openNotificationBar,
  closeNotificationBar,
  setNotificationBarHeight,
} from 'state/actions/applicationUIActions';

import BreadcrumbBar from 'components/BreadcrumbBar';
import PopUp from 'components/PopUp';

import {
  RouteMap,
  RoutesWithoutFooter,
  RoutesWithMinimalNav,
} from 'constants/Routes';

import { GlobalSettingsReducer } from 'state/reducers/globalSettings';
import { ArticleReducer } from 'state/reducers/article';
import { GlobalState, OrganizedTopic, TopicLink } from 'types';

interface StoreProps {
  articles: ArticleReducer;
  organizedTopics: OrganizedTopic;
  globalSettings: GlobalSettingsReducer;
  sideNavigationByOrder: string[];
  businessOnboardingPopUpIsActive: boolean;
  notificationBarIsActive: boolean | null;
  notificationBarHeight: number;
}

interface DispatchProps {
  actions: {
    openNotificationBar(): void;
    closeNotificationBar(): void;
    setNotificationBarHeight(height: number): void;
  };
}

interface PassedProps {
  children: ReactNode;
}

type Props = PassedProps & StoreProps & WithRouterProps & DispatchProps;

interface State {
  notificationBarIsDismissed: boolean;
}

class Wrapper extends PureComponent<Props, State> {
  notificationBar: RefObject<HTMLDivElement> = createRef();

  constructor(props: Props) {
    super(props);

    Router.events.on('routeChangeStart', () => NProgress.start());

    Router.events.on('routeChangeComplete', () => {
      this.openNotificationBar();
      NProgress.done();
    });

    Router.events.on('routeChangeError', () => NProgress.done());

    this.state = {
      notificationBarIsDismissed: false,
    };
  }

  componentDidMount() {
    this.openNotificationBar();

    window.addEventListener('resize', this.throttleGetNotificationBarHeight);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.throttleGetNotificationBarHeight);
  }

  getNotificationBarHeight = () => {
    if (this.notificationBar.current) {
      const height = this.notificationBar.current.clientHeight;

      this.props.actions.setNotificationBarHeight(height);
    }
  };

  throttleGetNotificationBarHeight = throttle(
    this.getNotificationBarHeight,
    500
  );

  openNotificationBar = () => {
    if (
      this.props.globalSettings.notificationBar.isActive &&
      !this.state.notificationBarIsDismissed
    ) {
      localForage.getItem('notificationBarDismissDate', (err, value) => {
        const difference = differenceInDays(
          new Date(),
          new Date(value as string)
        );

        if (difference >= 1) {
          this.props.actions.openNotificationBar();
          this.getNotificationBarHeight();
        }
      });
    }
  };

  closeNotificationBar = () => {
    this.props.actions.closeNotificationBar();
    this.setState({ notificationBarIsDismissed: true });
  };

  render() {
    const {
      articles,
      organizedTopics,
      children,
      globalSettings,
      sideNavigationByOrder,
      router,
      router: { pathname, query, asPath },
      businessOnboardingPopUpIsActive,
      notificationBarIsActive,
      notificationBarHeight,
    } = this.props;

    const topicSlugs = get(query, 'contentHubSlugs', []);
    const isContentHubPage = pathname.includes(
      RouteMap.CONTENT_HUB_LANDING.path
    );
    const article = get(articles, `/${topicSlugs.slice(-1)[0]}`, null);
    const contentHubSlugs = get(query, 'contentHubSlugs', []);
    const slug = !!contentHubSlugs.length
      ? `/${contentHubSlugs.slice(-1)[0]}`
      : '/';
    const currentTopic: TopicLink | undefined = get(
      organizedTopics,
      `topicsBySlug[${slug}]`
    );
    const articlePageViewIsShowingErrorPage =
      isContentHubPage && !article && !currentTopic;
    const hideFooter = RoutesWithoutFooter.some((basePath) =>
      router.pathname.includes(basePath)
    );
    const renderMinimalNav = RoutesWithMinimalNav.some((basePath) =>
      router.pathname.includes(basePath)
    );

    return (
      <>
        <GlobalStyle />
        <ThemeProvider theme={Theme}>
          <main className="App relative">
            {((notificationBarIsActive && notificationBarHeight !== 0) ||
              !notificationBarIsActive) && (
              <div
                className="relative"
                style={{
                  paddingTop: notificationBarIsActive
                    ? `${notificationBarHeight}px`
                    : '0',
                }}
              >
                <div className="relative">
                  <SideNav
                    router={router}
                    sideNavigation={globalSettings.sideNavigation}
                    sideNavigationByOrder={sideNavigationByOrder}
                    businessOnboardingPopUpIsActive={
                      businessOnboardingPopUpIsActive
                    }
                    notificationBarIsActive={notificationBarIsActive}
                    notificationBarHeight={notificationBarHeight}
                    renderMinimalNav={renderMinimalNav}
                  />
                  <div className="absolute w100 h100 z-breadcrumbs events-none">
                    <BreadcrumbBar
                      article={article}
                      organizedTopics={organizedTopics}
                      notificationBarIsActive={notificationBarIsActive}
                      notificationBarHeight={notificationBarHeight}
                    />
                  </div>
                  <div
                    className={cx('App__page relative flex flex-col', {
                      'App__page--with-minimal-nav': renderMinimalNav,
                      'App__page--with-breadcrumb':
                        (isContentHubPage || currentTopic) &&
                        !articlePageViewIsShowingErrorPage,
                    })}
                  >
                    {children}
                  </div>
                </div>
                {!hideFooter && (
                  <Footer
                    footer={globalSettings.footer}
                    pageHasMinimalNav={renderMinimalNav}
                  />
                )}
              </div>
            )}
            <PopUp popUp={globalSettings.popUp} />
            <BusinessOnboardingPopUp />
            <NotificationBar
              closeNotificationBar={this.closeNotificationBar}
              elemRef={this.notificationBar}
              notificationBar={globalSettings.notificationBar}
              notificationBarIsActive={notificationBarIsActive}
            />
          </main>
        </ThemeProvider>
      </>
    );
  }
}

const mapStateToProps = (state: GlobalState): StoreProps => ({
  articles: state.article,
  organizedTopics: organizedTopic(state),
  globalSettings: state.globalSettings,
  sideNavigationByOrder: sideNavigationByOrder(state),
  businessOnboardingPopUpIsActive:
    state.applicationUI.businessOnboardingPopUpIsActive,
  notificationBarIsActive: state.applicationUI.notificationBarIsActive,
  notificationBarHeight: state.applicationUI.notificationBarHeight,
});

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
  actions: bindActionCreators(
    {
      openNotificationBar,
      closeNotificationBar,
      setNotificationBarHeight,
    },
    dispatch
  ),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(Wrapper));
